import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const chunkedPrefillComponent: ComponentDef = {
  id: 'chunked-prefill',
  name: 'Chunked Prefill',
  description: 'Split long prompts into fixed-size chunks and interleave with decode tokens each iteration — balances GPU compute utilisation while bounding per-step latency.',
  category: 'optimization',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'prompt_len', label: 'Prompt length', type: 'number', default: 2048, min: 1, max: 32768, step: 1 },
    { name: 'chunk_size', label: 'Chunk size', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'decode_batch', label: 'Decode batch size', type: 'number', default: 8, min: 1, max: 256, step: 1 }
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const prompt_len = Number(params.prompt_len);
    const chunk_size = Number(params.chunk_size);
    const decode_batch = Number(params.decode_batch);
    const n_chunks = Math.ceil(prompt_len / chunk_size);

    const g = new GraphBuilder();
    g.input('prompt', [prompt_len, d_model]);
    g.input('decode_tokens', [decode_batch, d_model]);

    // ── Split prompt into chunks ──
    g.split('prompt_chunks', 'prompt', [chunk_size, d_model], `${n_chunks} chunks × ${chunk_size} tokens`);

    // ── One iteration: process one prompt chunk + all decode tokens together ──
    g.concat('mixed_batch', ['prompt_chunks', 'decode_tokens'], [chunk_size + decode_batch, d_model]);

    // Prefill chunk uses full context attention (attending to KV cache of prior chunks)
    g.cache('kv_prefix', 'prompt_chunks', [chunk_size, d_model]);

    // Decode tokens attend to full accumulated KV cache
    g.cache('kv_decode', 'decode_tokens', [decode_batch, d_model]);

    // ── Forward pass on mixed batch ──
    g.linear('qkv_proj', 'mixed_batch', [d_model, d_model * 3]);
    g.reshape('qkv_split', 'qkv_proj', [chunk_size + decode_batch, d_model]);

    // Attention: prefill tokens attend to [past_kv + chunk_kv], decode tokens attend to full kv
    g.concat('full_kv', ['kv_prefix', 'kv_decode'], [chunk_size + decode_batch, d_model]);
    g.transpose('kv_T', 'full_kv', [d_model, chunk_size + decode_batch], 'K^T');
    g.matmul('attn_scores', 'qkv_split', 'kv_T', [chunk_size + decode_batch, chunk_size + decode_batch]);
    g.softmax('attn_w', 'attn_scores', -1);
    g.matmul('attn_out', 'attn_w', 'full_kv', [chunk_size + decode_batch, d_model]);
    g.linear('out_proj', 'attn_out', [d_model, d_model]);

    // ── Update KV cache and append decoded tokens ──
    g.add('kv_updated', 'kv_prefix', 'out_proj');
    g.output('out', 'kv_updated');

    return g.build();
  }
};

export const disaggregatedPrefillComponent: ComponentDef = {
  id: 'disaggregated-prefill',
  name: 'Disaggregated Prefill',
  description: 'Prefill runs on dedicated P-nodes (compute-bound); KV cache is transferred via RDMA/NVLink to D-nodes (memory-bandwidth-bound) for decode. Eliminates prefill-decode interference.',
  category: 'optimization',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'prompt_len', label: 'Prompt length', type: 'number', default: 2048, min: 1, max: 32768, step: 1 },
    { name: 'decode_len', label: 'Decode length', type: 'number', default: 128, min: 1, max: 4096, step: 1 },
    { name: 'n_layers', label: 'KV layers transferred', type: 'number', default: 32, min: 1, max: 128, step: 1 }
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const prompt_len = Number(params.prompt_len);
    const n_layers = Number(params.n_layers);
    const kv_size = n_layers * 2;  // K and V per layer

    const g = new GraphBuilder();

    // ── P-Node: prefill phase (compute-intensive) ──
    g.input('prompt', [prompt_len, d_model]);
    g.linear('prefill_qkv', 'prompt', [d_model, d_model * 3]);
    g.reshape('prefill_kv', 'prefill_qkv', [prompt_len, d_model * 2]);
    g.cache('kv_cache_pnode', 'prefill_kv', [n_layers, prompt_len, d_model]);
    g.linear('prefill_out', 'prefill_qkv', [d_model, d_model]);

    // ── KV Migration: transfer KV blocks over high-bandwidth interconnect ──
    // Each KV block is a paged-attention physical block
    g.reshape('kv_blocks', 'kv_cache_pnode', [kv_size, d_model]);
    g.p2p('kv_migrated', 'kv_blocks', [kv_size, d_model]);   // RDMA / NVLink transfer

    // ── D-Node: decode phase (memory-bandwidth-bound) ──
    g.input('new_token', [1, d_model]);
    g.linear('decode_qkv', 'new_token', [d_model, d_model * 3]);
    g.reshape('decode_q', 'decode_qkv', [1, d_model]);

    // Decode attends to migrated KV cache
    g.cache('kv_cache_dnode', 'kv_migrated', [n_layers, prompt_len, d_model]);
    g.transpose('kv_T', 'kv_cache_dnode', [d_model, prompt_len], 'K^T');
    g.matmul('decode_attn', 'decode_q', 'kv_T', [1, prompt_len]);
    g.softmax('decode_attn_w', 'decode_attn', -1);
    g.matmul('decode_out', 'decode_attn_w', 'kv_cache_dnode', [1, d_model]);
    g.linear('next_token_logits', 'decode_out', [d_model, d_model]);
    g.output('out', 'next_token_logits');

    return g.build();
  }
};
